/** Karte — Leaflet + CartoDB Dark Matter tiles (free, no token) + custom
 *  DivIcon piny + side panel. 1:1 z prototype/screens.jsx Karte. */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Ic } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/badge";
import {
  fetchMapPins,
  fetchPartnerVehicles,
  geocodePins,
  type MapPin,
  type PartnerVehiclePin,
} from "@/lib/fetchers";
import { fmtEur } from "@/lib/format";
import { orderStatusMeta } from "@/lib/status";
import { queryKeys } from "@/lib/queries";
import { t } from "@/i18n/de";

const DE_CENTER: [number, number] = [51.0, 10.5];
const DE_ZOOM = 5.4;

// Dark tiles — CartoDB Dark Matter (free, attribution required)
const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const DARK_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';

type LayerKey = "active" | "planned" | "planned_far" | "done" | "vehicles";
type SelectedRef = { kind: "pin"; data: MapPin } | { kind: "vehicle"; data: PartnerVehiclePin };

function pinIcon(kind: MapPin["kind"]): L.DivIcon {
  if (kind === "done") {
    return L.divIcon({
      className: "portal-map-pin",
      html: `<div style="width:10px;height:10px;border-radius:999px;background:transparent;border:2px solid var(--text-3);box-shadow:0 0 0 2px rgba(0,0,0,0.5);"></div>`,
      iconSize: [12, 12],
      iconAnchor: [6, 6],
    });
  }
  // active = tento týden (gold s pulse halo), planned = příští týden (gold-lo),
  // planned_far = budoucí (blue, bez halo)
  const tone =
    kind === "active" ? "var(--gold)" : kind === "planned" ? "var(--gold-lo)" : "var(--blue)";
  const halo =
    kind === "active"
      ? `<span style="position:absolute;left:-10px;top:-10px;width:32px;height:32px;border-radius:999px;background:var(--gold);opacity:0.25;animation:portal-pulse 1.6s ease-out infinite;"></span>`
      : "";
  return L.divIcon({
    className: "portal-map-pin",
    html: `<div style="position:relative;">${halo}<div style="position:relative;width:14px;height:14px;border-radius:999px;background:${tone};border:2px solid ${tone};box-shadow:0 0 0 2px rgba(0,0,0,0.5);"></div></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

function vehicleIcon(): L.DivIcon {
  return L.divIcon({
    className: "portal-map-pin",
    html:
      '<div style="width:28px;height:28px;border-radius:8px;background:var(--bg-elevated);border:1px solid var(--gold);display:flex;align-items:center;justify-content:center;color:var(--gold);box-shadow:0 0 0 4px rgba(201,169,107,0.15);">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="11" height="9" rx="1"/><path d="M13 10h5l3 3v3h-8"/><circle cx="6" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>' +
      "</div>",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function FitToVisible({ pins, vehicles }: { pins: MapPin[]; vehicles: PartnerVehiclePin[] }) {
  const map = useMap();
  const all = useMemo(() => {
    const pts: [number, number][] = [];
    pins.forEach((p) => {
      if (typeof p.lat === "number" && typeof p.lng === "number") pts.push([p.lat, p.lng]);
    });
    vehicles.forEach((v) => {
      if (typeof v.lat === "number" && typeof v.lng === "number") pts.push([v.lat, v.lng]);
    });
    return pts;
  }, [pins, vehicles]);
  if (all.length === 0) return null;
  if (all.length === 1) {
    map.setView(all[0], 12);
  } else {
    map.fitBounds(L.latLngBounds(all), { padding: [60, 60] });
  }
  return null;
}

export function MapRoute() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<SelectedRef | null>(null);
  const [enabledLayers, setEnabledLayers] = useState<Record<LayerKey, boolean>>({
    active: true,
    planned: true,
    planned_far: false,
    done: true,
    vehicles: true,
  });

  const pinsQ = useQuery({
    queryKey: queryKeys.mapPins,
    queryFn: async () => {
      const raw = await fetchMapPins();
      return geocodePins(raw);
    },
    staleTime: 5 * 60 * 1000,
  });
  const vehiclesQ = useQuery({
    queryKey: queryKeys.mapVehicles,
    queryFn: fetchPartnerVehicles,
    refetchInterval: 30 * 1000,
  });

  const visiblePins = useMemo(() => {
    const pins = pinsQ.data || [];
    return pins.filter((p) => {
      if (!Number.isFinite(p.lat) || !Number.isFinite(p.lng)) return false;
      if (p.kind === "active" && !enabledLayers.active) return false;
      if (p.kind === "planned" && !enabledLayers.planned) return false;
      if (p.kind === "planned_far" && !enabledLayers.planned_far) return false;
      if (p.kind === "done" && !enabledLayers.done) return false;
      return true;
    });
  }, [pinsQ.data, enabledLayers]);

  const visibleVehicles = useMemo(
    () =>
      enabledLayers.vehicles
        ? (vehiclesQ.data || []).filter(
            (v) => typeof v.lat === "number" && typeof v.lng === "number",
          )
        : [],
    [vehiclesQ.data, enabledLayers.vehicles],
  );

  return (
    <div style={{ height: "100%", position: "relative", background: "#06090D" }}>
      <MapContainer
        center={DE_CENTER}
        zoom={DE_ZOOM}
        style={{ position: "absolute", inset: 0, background: "#06090D" }}
        attributionControl
      >
        <TileLayer attribution={DARK_ATTR} url={DARK_TILES} />
        <FitToVisible pins={visiblePins} vehicles={visibleVehicles} />
        {visiblePins.map((p) => (
          <Marker
            key={p.id}
            position={[p.lat, p.lng]}
            icon={pinIcon(p.kind)}
            eventHandlers={{
              click: () => setSelected({ kind: "pin", data: p }),
            }}
          />
        ))}
        {visibleVehicles.map((v) => (
          <Marker
            key={v.vehicle_id}
            position={[v.lat, v.lng]}
            icon={vehicleIcon()}
            eventHandlers={{
              click: () => setSelected({ kind: "vehicle", data: v }),
            }}
          />
        ))}
      </MapContainer>

      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pointerEvents: "none",
          zIndex: 1000,
          gap: 16,
        }}
      >
        <div
          className="card"
          style={{
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            pointerEvents: "auto",
          }}
        >
          <Ic.Map size={14} />
          <span style={{ fontSize: 13, fontWeight: 500 }}>{t.map.title}</span>
          <span style={{ fontSize: 11.5, color: "var(--text-3)" }}>
            · {t.map.pinsCount(visiblePins.length + visibleVehicles.length)}
          </span>
        </div>
        <div className="card" style={{ padding: 8, pointerEvents: "auto" }}>
          <div
            style={{
              fontSize: 10.5,
              color: "var(--text-3)",
              padding: "4px 8px 6px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            {t.map.layers}
          </div>
          <LayerToggle
            label="Diese Woche"
            checked={enabledLayers.active}
            onChange={(v) => setEnabledLayers((s) => ({ ...s, active: v }))}
          />
          <LayerToggle
            label="Nächste Woche"
            checked={enabledLayers.planned}
            onChange={(v) => setEnabledLayers((s) => ({ ...s, planned: v }))}
          />
          <LayerToggle
            label="Geplant (später)"
            checked={enabledLayers.planned_far}
            onChange={(v) => setEnabledLayers((s) => ({ ...s, planned_far: v }))}
          />
          <LayerToggle
            label="Abgeschlossen"
            checked={enabledLayers.done}
            onChange={(v) => setEnabledLayers((s) => ({ ...s, done: v }))}
          />
          <LayerToggle
            label={t.map.layerVehicles}
            checked={enabledLayers.vehicles}
            onChange={(v) => setEnabledLayers((s) => ({ ...s, vehicles: v }))}
            liveBadge
          />
        </div>
      </div>

      <div
        className="card"
        style={{
          position: "absolute",
          bottom: 16,
          left: 16,
          padding: 12,
          display: "flex",
          gap: 16,
          fontSize: 11.5,
          zIndex: 1000,
        }}
      >
        <Legend dotColor="var(--gold)" label="Diese Woche" />
        <Legend dotColor="var(--gold-lo)" label="Nächste" />
        <Legend dotColor="var(--blue)" label="Später" />
        <Legend dotColor="var(--text-3)" label="Erledigt" small />
        <Legend label={t.map.layerVehicles} icon />
      </div>

      {selected && (
        <SidePanel
          selected={selected}
          onClose={() => setSelected(null)}
          onOpenOrder={(id) => navigate(`/orders/${id}`)}
        />
      )}
    </div>
  );
}

function LayerToggle({
  label,
  checked,
  onChange,
  liveBadge,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  liveBadge?: boolean;
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        fontSize: 12.5,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: "var(--gold)" }}
      />
      <span>{label}</span>
      {liveBadge && (
        <span className="badge badge-gold badge-pulse" style={{ marginLeft: "auto" }}>
          Live
        </span>
      )}
    </label>
  );
}

function Legend({
  dotColor,
  label,
  small = false,
  icon = false,
}: {
  dotColor?: string;
  label: string;
  small?: boolean;
  icon?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon ? (
        <Ic.Truck size={12} />
      ) : (
        <span
          style={{
            width: small ? 6 : 10,
            height: small ? 6 : 10,
            borderRadius: 999,
            background: dotColor,
            border: small ? "none" : `2px solid ${dotColor}`,
          }}
        />
      )}
      <span>{label}</span>
    </div>
  );
}

function SidePanel({
  selected,
  onClose,
  onOpenOrder,
}: {
  selected: SelectedRef;
  onClose: () => void;
  onOpenOrder: (orderId: string) => void;
}) {
  if (selected.kind === "vehicle") {
    const v = selected.data;
    return (
      <div
        className="card"
        style={{
          position: "absolute",
          right: 16,
          top: 70,
          width: 320,
          padding: 18,
          zIndex: 1000,
          boxShadow: "var(--shadow-pop)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <span className="badge badge-gold badge-pulse">Live</span>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            style={{ padding: 4 }}
            aria-label="Schließen"
          >
            <Ic.Close size={14} />
          </button>
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-3)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: 4,
          }}
        >
          Fahrzeug
        </div>
        <div className="num" style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>
          {v.vehicle_label || v.vehicle_id.slice(0, 8)}
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>
          unterwegs zu {v.installation_city || "—"}
        </div>
        <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--text-3)" }}>
          {t.map.speed(v.speed_kmh || 0)} · {t.map.lastUpdate(secondsAgo(v.recorded_at))}
        </div>
        {v.deal_id && (
          <Button
            variant="primary"
            size="sm"
            style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
            onClick={() => onOpenOrder(v.deal_id)}
          >
            {t.map.pinSelectAction} <Ic.ArrowRight size={12} />
          </Button>
        )}
      </div>
    );
  }

  const p = selected.data;
  return (
    <div
      className="card"
      style={{
        position: "absolute",
        right: 16,
        top: 70,
        width: 320,
        padding: 18,
        zIndex: 5,
        boxShadow: "var(--shadow-pop)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <StatusBadge meta={orderStatusMeta(p.status)} />
        <button
          type="button"
          onClick={onClose}
          className="btn btn-ghost btn-sm"
          style={{ padding: 4 }}
          aria-label="Schließen"
        >
          <Ic.Close size={14} />
        </button>
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
        {p.client || p.title || "—"}
      </div>
      <div style={{ fontSize: 13, color: "var(--text-2)", marginBottom: 14 }}>{p.city || "—"}</div>
      <div className="num" style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 14 }}>
        {p.title || p.id.slice(0, 8)}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 12,
          borderTop: "1px solid var(--border-subtle)",
        }}
      >
        <span style={{ fontSize: 12, color: "var(--text-3)" }}>Wert</span>
        <span className="num" style={{ color: "var(--gold-hi)", fontWeight: 600 }}>
          {fmtEur(p.value)}
        </span>
      </div>
      <Button
        variant="primary"
        size="sm"
        style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
        onClick={() => onOpenOrder(p.id)}
      >
        {t.map.pinSelectAction} <Ic.ArrowRight size={12} />
      </Button>
    </div>
  );
}

function secondsAgo(iso: string): number {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.round(diff / 1000));
}
